/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import React, { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Alert from "../common/Alert";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { makeRequest } from "@/utils/axiosInterceptor";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import getHospitalData from "../hospital/getHospitalData";
import { toast } from "sonner";
import { isValidDateRange } from "@/utils/formatDateForInput";

const getDefaultDates = () => {
  const today = new Date();
  const oneYearLater = new Date(
    today.getFullYear() + 1,
    today.getMonth(),
    today.getDate(),
  );

  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return {
    activation_date: formatDateForInput(today),
    expiry_date: formatDateForInput(oneYearLater),
  };
};

interface ApiCreateData {
  hospital_clinic_id: number | null;
  api_access: boolean;
  allowed_requests: number;
  abnormal_model_access: boolean;
  tb_model_access: boolean;
  consolidation_model_access: boolean;
  effusion_model_access: boolean;
  cardiomegaly_model_access: boolean;
  pneumothorax_model_access: boolean;
  rheumatic_model_access: boolean;
  edema_model_access: boolean;
  atelectasis_model_access: boolean;
  activation_date: string;
  expiry_date: string;
  segregation_model_access: boolean;
}

const defaultApiData: ApiCreateData = {
  hospital_clinic_id: null,
  api_access: true,
  allowed_requests: 200,
  abnormal_model_access: true,
  tb_model_access: true,
  consolidation_model_access: true,
  effusion_model_access: true,
  cardiomegaly_model_access: true,
  pneumothorax_model_access: true,
  rheumatic_model_access: false,
  edema_model_access: true,
  atelectasis_model_access: true,
  ...getDefaultDates(),
  segregation_model_access: true,
};

export default function ApiCreateForm({ setAddHospitalSheet }) {
  const queryClient = useQueryClient();
  const { data: hospitals = [] } = useQuery({
    queryKey: ["hospitals"],
    queryFn: getHospitalData,
    refetchIntervalInBackground: true,
    refetchInterval: 300000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const [apiData, setApiData] = useState<ApiCreateData>(defaultApiData);
  const [currentStep, setCurrentStep] = useState(1);

  const createApi = async () => {
    try {
      if (!apiData.hospital_clinic_id) {
        toast.error("Please select a hospital/clinic");
        return;
      }
      if (!isValidDateRange(apiData.activation_date, apiData.expiry_date)) {
        toast.error('Start must be before end');
        return;
      }

      const payload = {
        ...apiData,
        activation_date: apiData.activation_date
          ? new Date(apiData.activation_date).toISOString()
          : null,
        expiry_date: apiData.expiry_date
          ? new Date(apiData.expiry_date).toISOString()
          : null,
      };

      const res = await makeRequest("POST", "/apis", payload);
      console.log('response from create API:->>>>', res);

      if (res?.statusCode === 400) {
        toast.error(res?.message || "Failed to create API");
        setActivateAlert(false);
        return;
      }

      if (res?.status === 201) {
        toast.success("API created successfully!");
        queryClient.invalidateQueries({
          queryKey: ["apis"],
        });
        setApiData(defaultApiData);
        setCurrentStep(1);
        setAddHospitalSheet(false);
      }
    } catch (error) {
      console.log(error);
      toast.error("An error occurred while creating the API");
    }
  };

  const modelAccessFields = [
    { key: "abnormal_model_access", label: "Abnormal" },
    { key: "tb_model_access", label: "TB" },
    { key: "atelectasis_model_access", label: "Atelectasis" },
    { key: "edema_model_access", label: "Edema" },
    { key: "cardiomegaly_model_access", label: "Cardiomegaly" },
    { key: "consolidation_model_access", label: "Consolidation" },
    { key: "rheumatic_model_access", label: "Rheumatic" },
    { key: "pneumothorax_model_access", label: "Pneumothorax" },
    { key: "effusion_model_access", label: "Effusion" },
    { key: "segregation_model_access", label: "Segregation" },
  ] as const;

  const [activateAlert, setActivateAlert] = useState(false);
  return (
    <div className="w-full">
      {/* Step 1: Hospital/Clinic Selection & Basic Settings */}
      {currentStep === 1 && (
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right">Hospital/Clinic</Label>
            <Select
              value={apiData.hospital_clinic_id?.toString() || ""}
              onValueChange={(value) =>
                setApiData({ ...apiData, hospital_clinic_id: parseInt(value) })
              }
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Select Hospital/Clinic" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Available Hospitals/Clinics</SelectLabel>
                  {hospitals.map((hospital: any) => (
                    <SelectItem
                      key={hospital.id}
                      value={hospital.id.toString()}
                    >
                      {hospital.name || `Hospital ${hospital.id}`}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label className="text-right">API Access</Label>
            <div className="col-span-3 flex items-center gap-2">
              <Checkbox
                checked={apiData.api_access}
                onCheckedChange={(val) =>
                  setApiData({ ...apiData, api_access: val as boolean })
                }
                id="api_access"
              />
              <label
                htmlFor="api_access"
                className="text-sm font-medium cursor-pointer"
              >
                Enable API Access
              </label>
            </div>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="allowed_requests" className="text-right">
              Allowed Tokens
            </Label>
            <Input
              id="allowed_requests"
              type="number"
              placeholder="0"
              value={apiData.allowed_requests}
              onChange={(e) =>
                setApiData({
                  ...apiData,
                  allowed_requests: parseInt(e.target.value) || 0,
                })
              }
              className="col-span-3"
            />
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="activation_date" className="text-right">
              Activation Date
            </Label>
            <Input
              id="activation_date"
              type="datetime-local"
              value={apiData.activation_date}
              onChange={(e) =>
                setApiData({ ...apiData, activation_date: e.target.value })
              }
              className="col-span-3"
            />
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="expiry_date" className="text-right">
              Expiry Date
            </Label>
            <Input
              id="expiry_date"
              type="datetime-local"
              value={apiData.expiry_date}
              onChange={(e) =>
                setApiData({ ...apiData, expiry_date: e.target.value })
              }
              className="col-span-3"
            />
          </div>

          <Separator className="my-4" />

          <div className="flex justify-between">
            <Button variant="outline" disabled>
              Back
            </Button>
            <Button onClick={() => setCurrentStep(2)}>
              Next - Model Access
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Model Access Selection */}
      {currentStep === 2 && (
        <div className="grid gap-4 py-4">
          <h3 className="font-bold text-lg">Select Model Access</h3>
          <p className="text-sm text-gray-600">
            Enable/disable access to medical AI models for this API
          </p>
          <Separator className="my-2" />

          <div className="grid grid-cols-2 gap-4">
            {modelAccessFields.map((field) => (
              <div
                key={field.key}
                className="flex items-center gap-2 p-3 border rounded-lg hover:bg-gray-50"
              >
                <Checkbox
                  checked={apiData[field.key as keyof ApiCreateData] as boolean}
                  onCheckedChange={(val) =>
                    setApiData({ ...apiData, [field.key]: val as boolean })
                  }
                  id={field.key}
                />
                <label
                  htmlFor={field.key}
                  className="text-sm font-medium cursor-pointer flex-1"
                >
                  {field.label}
                </label>
              </div>
            ))}
          </div>

          <Separator className="my-4" />

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setCurrentStep(1)}>
              Back
            </Button>
            <Button
              onClick={() => setActivateAlert(true)}
              disabled={!apiData.hospital_clinic_id}
            >
              Create API
            </Button>
          </div>
        </div>
      )}

      <Alert
        open={activateAlert}
        setOpen={setActivateAlert}
        title="Create API"
        description="Are you sure you want to create this API configuration?"
        action={createApi}
      />
    </div>
  );
}
